import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { IntakeData } from "@/lib/types";
import { Shield, Loader2, Mail, RefreshCw, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface VerifyEmailStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onVerificationSuccess: () => void;
  onBack: () => void;
  formUid?: string;
  referenceUid?: string;
}

type AuthMode = "magic-link" | "otp";

const OTP_EXPIRY_SECONDS = 15 * 60;

export default function VerifyEmailStep({
  data,
  onUpdate,
  onVerificationSuccess,
  onBack,
  formUid,
  referenceUid,
}: VerifyEmailStepProps) {
  const { i18n } = useLingui();
  const [authMode, setAuthMode] = useState<AuthMode>("magic-link");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [lastMagicLinkSentAt, setLastMagicLinkSentAt] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const initializedRef = useRef(false);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const formattedMagicLinkTime = lastMagicLinkSentAt
    ? new Date(lastMagicLinkSentAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  useEffect(() => {
    if (!initializedRef.current && formUid && referenceUid) {
      initializedRef.current = true;
      void sendMagicLink();
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [formUid, referenceUid]);

  useEffect(() => {
    if (timeLeft <= 0) {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    countdownRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}:${remainder.toString().padStart(2, "0")}`;
  };

  const sendMagicLink = async (options?: { forceResend?: boolean }) => {
    if (!formUid || !referenceUid) {
      setError(i18n._(t`Unable to send verification link. Missing intake reference.`));
      setAuthMode("otp");
      return;
    }

    setIsSending(true);
    setError("");
    setStatusMessage("");

    try {
      const result = await patientIntakeApi.startAuth({
        email: data.email,
        formUid,
        referenceUid,
        forceResend: options?.forceResend ?? false,
      });

      setLastMagicLinkSentAt(result.data?.sentAt ?? new Date().toISOString());

      if (result.data?.alreadySent) {
        setStatusMessage(
          i18n._(
            t`We've already sent a verification link to your email. Please check your inbox (including spam).`,
          ),
        );
      } else if (options?.forceResend) {
        setStatusMessage(
          i18n._(t`A fresh verification link is on its way. Check your email and click the link to finish.`),
        );
      } else if (result.data?.requiresOtpFallback) {
        setStatusMessage(
          i18n._(t`We emailed you a magic link. If you prefer, you can verify with a 6-digit code instead.`),
        );
      } else {
        setStatusMessage(
          i18n._(t`We just sent a secure verification link to your email. Click it to finish your intake.`),
        );
      }

      setAuthMode("magic-link");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`We couldn't send the verification link. We'll try a code instead.`);
      setError(errorMessage);
      setAuthMode("otp");
      // Automatically fallback to OTP if the link fails
      setTimeout(() => {
        void sendOtp();
      }, 800);
    } finally {
      setIsSending(false);
    }
  };

  const sendOtp = async () => {
    if (!formUid) {
      setError(i18n._(t`Unable to send verification code. Missing intake reference.`));
      return;
    }

    setIsSending(true);
    setAuthMode("otp");
    setError("");
    setStatusMessage("");
    setOtp("");
    setRemainingAttempts(null);

    try {
      const result = await patientIntakeApi.sendVerificationCode(data.email, formUid);
      setChallengeId(result.data.challengeId);
      setTimeLeft(OTP_EXPIRY_SECONDS);
      setStatusMessage(
        i18n._(t`Verification code sent! Check your email (and spam folder) for a 6-digit code.`),
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`We couldn't send the code right now. Please try again shortly.`);

      const minutesMatch = errorMessage.match(/(\d+)\s+minutes?/);
      const secondsMatch = errorMessage.match(/(\d+)\s+seconds?/);

      if (minutesMatch) {
        setTimeLeft(parseInt(minutesMatch[1], 10) * 60);
      } else if (secondsMatch) {
        setTimeLeft(parseInt(secondsMatch[1], 10));
      } else if (errorMessage.includes("few seconds")) {
        setTimeLeft(30);
      } else {
        setTimeLeft(0);
      }

      setError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!formUid) {
      setError(i18n._(t`We couldn't verify your code because the intake reference is missing.`));
      return;
    }

    if (otp.length !== 6) {
      setError(i18n._(t`Please enter the 6-digit code that we emailed you.`));
      return;
    }

    setIsVerifying(true);
    setError("");
    setStatusMessage("");

    try {
      const result = await patientIntakeApi.verifyEmail(
        data.email,
        otp,
        formUid,
        challengeId,
      );

      if (result.data?.accessToken) {
        localStorage.setItem("accessToken", result.data.accessToken);
        if (result.data.refreshToken) {
          localStorage.setItem("refreshToken", result.data.refreshToken);
        }
      }

      onUpdate({ emailVerified: true });
      setStatusMessage(i18n._(t`Email verified successfully! Redirecting to the final step...`));

      setTimeout(() => {
        onVerificationSuccess();
      }, 1200);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n._(t`The verification code didn't work. Please try again.`);

      const attemptsMatch = errorMessage.match(/You have (\d+) attempt/);
      if (attemptsMatch) {
        setRemainingAttempts(parseInt(attemptsMatch[1], 10));
      } else {
        setRemainingAttempts(null);
      }

      if (errorMessage.toLowerCase().includes("expired")) {
        setTimeLeft(0);
      }

      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (value: string) => {
    const numeric = value.replace(/\D/g, "").slice(0, 6);
    setOtp(numeric);
    if (error) {
      setError("");
    }
    setRemainingAttempts(null);
  };

  const handleSwitchToOtp = () => {
    setAuthMode("otp");
    setError("");
    setStatusMessage("");
    if (!challengeId) {
      void sendOtp();
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4 py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-4">
          <Shield className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
          <Trans>Verify Your Email</Trans>
        </h1>
        <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
          <Trans>
          {authMode === "magic-link"
            ? i18n._(
                t`We just sent a secure link to your inbox. Click it to confirm your email, or switch to a verification code.`,
              )
            : i18n._(t`Enter the 6-digit verification code we emailed you to finish confirming your email.`)}
          </Trans>
        </p>
        <p className="text-sm text-gray-600 font-semibold">{data.email}</p>
      </div>
      
      <Card className="border-0 shadow-xl">
        <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6">
          {authMode === "magic-link" ? (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#166B93]/10 to-[#CEF12F]/10 rounded-full flex items-center justify-center">
                  {isSending ? (
                    <Loader2 className="h-10 w-10 text-[#166B93] animate-spin" />
                  ) : (
                    <Mail className="h-10 w-10 text-[#166B93]" />
                  )}
                </div>
              </div>

              {statusMessage && (
                <div className="bg-[#166B93]/10 border border-[#166B93]/20 rounded-xl p-4 text-[#0F4461] text-sm leading-relaxed">
                  {statusMessage}
                  {formattedMagicLinkTime && (
                    <div className="mt-2 text-xs text-[#0F4461]/80">
                      <Trans>Sent at {formattedMagicLinkTime}</Trans>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={() => sendMagicLink({ forceResend: true })}
                  disabled={isSending}
                  className="w-full h-12 text-base bg-gradient-to-r from-[#166B93] to-[#166B93]/90 hover:from-[#166B93]/90 hover:to-[#166B93]/80 shadow-md"
                >
                  {isSending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <Trans>Sending...</Trans>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5" />
                      <Trans>Resend Magic Link</Trans>
                    </div>
                  )}
                </Button>

                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">
                    <Trans>Prefer a verification code instead?</Trans>
                  </p>
                  <Button
                    variant="outline"
                    className="w-full h-12 text-base border-[#166B93] text-[#166B93]"
                    onClick={handleSwitchToOtp}
                  >
                    <Trans>Use Verification Code</Trans>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-2">
                <Label htmlFor="otp" className="text-sm font-medium text-gray-700">
                  <Trans>Verification Code</Trans>
                </Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(event) => handleOtpChange(event.target.value)}
                  placeholder={i18n._(t`Enter 6-digit code`)}
                  className="h-14 text-center text-2xl tracking-[1.2rem] font-bold border-2 border-gray-200 focus:border-[#166B93]"
                  maxLength={6}
                  autoComplete="one-time-code"
                  disabled={isVerifying}
                />
                {timeLeft > 0 && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <Trans>
                      Code expires in {formatTime(timeLeft)}
                    </Trans>
                  </div>
                )}
              </div>

              {statusMessage && !error && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 mt-0.5" />
                  <span>{statusMessage}</span>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-0.5" />
                  <div>
                    <p>{error}</p>
                    {remainingAttempts !== null && (
                      <p className="mt-2 text-xs text-red-600">
                        {remainingAttempts === 0
                          ? i18n._(t`No attempts remaining. Please request a new code.`)
                          : i18n._(
                              t`{count, plural, one {You have # attempt left.} other {You have # attempts left.}}`,
                              { count: remainingAttempts },
                            )}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={verifyOtp}
                  disabled={isVerifying || otp.length !== 6}
                  className="w-full h-12 text-base bg-gradient-to-r from-[#166B93] to-[#166B93]/90 hover:from-[#166B93]/90 hover:to-[#166B93]/80 shadow-md"
                >
                  {isVerifying ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <Trans>Verifying...</Trans>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      <Trans>Verify Email</Trans>
                    </div>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSending}
                  className="w-full h-12 text-base text-[#166B93]"
                  onClick={() => void sendOtp()}
                >
                  {isSending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <Trans>Resending code...</Trans>
                    </div>
                  ) : (
                    <Trans>Resend Code</Trans>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 text-base border-[#166B93] text-[#166B93]"
                  onClick={() => {
                    setAuthMode("magic-link");
                    void sendMagicLink();
                  }}
                >
                  <Trans>Use Magic Link Instead</Trans>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-12 px-8 text-base border-gray-300 text-gray-700"
        >
          <Trans>Back to Review</Trans>
        </Button>
      </div>
    </div>
  );
}
